import {useCallback, useEffect, useState} from 'react';

/**
 * Session-scoped, per-screen filter customisation.
 *
 * Tracks which optional ("Add a filter") filters a user has added on a given
 * screen. State is:
 *  - per screen (AC9): keyed by `screenKey`, so Participants / Scheduler /
 *    Schedule customisations are independent;
 *  - user-specific (AC7): it lives only in this browser session;
 *  - session-scoped (AC6): persisted to `sessionStorage` so it survives a
 *    page refresh within the session, but is dropped on logout (cleared
 *    explicitly on sign-out) and when the tab closes.
 */
const STORAGE_PREFIX = 'onedata_filter_customization_';

export const filterCustomizationStorageKey = (screenKey: string): string =>
  `${STORAGE_PREFIX}${screenKey}`;

/** The browser sessionStorage, or null when unavailable (SSR / tests / errors). */
const getSessionStorage = (): Storage | null => {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
};

/** Remove every screen's saved filter customisation (call on logout). */
export const clearAllFilterCustomizations = (): void => {
  const storage = getSessionStorage();
  if (!storage) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => storage.removeItem(key));
  } catch {
    /* ignore storage errors */
  }
};

const readStored = (screenKey: string): string[] => {
  const storage = getSessionStorage();
  if (!storage) return [];
  try {
    const raw = storage.getItem(filterCustomizationStorageKey(screenKey));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === 'string')
      : [];
  } catch {
    return [];
  }
};

export function useSessionFilterCustomization(screenKey: string) {
  const [addedFilterIds, setAddedFilterIds] = useState<string[]>(() =>
    readStored(screenKey),
  );

  // Re-read when the screen changes (e.g. navigating between screens that
  // share this hook instance is uncommon, but keep state in sync regardless).
  useEffect(() => {
    setAddedFilterIds(readStored(screenKey));
  }, [screenKey]);

  const persistAddedFilterIds = useCallback(
    (ids: string[]) => {
      setAddedFilterIds(ids);
      const storage = getSessionStorage();
      if (!storage) return;
      try {
        storage.setItem(
          filterCustomizationStorageKey(screenKey),
          JSON.stringify(ids),
        );
      } catch {
        /* ignore storage errors */
      }
    },
    [screenKey],
  );

  return {addedFilterIds, setAddedFilterIds: persistAddedFilterIds};
}
